import { useQuery } from '@tanstack/react-query'
import { Zap, Image, Film, Mic, RefreshCw } from 'lucide-react'
import { fetchDashboardStats, fetchRecentMedia } from '@/lib/supabase'
import { Badge, Card, Spinner, Button } from '@/components/ui'
import type { MediaItem } from '@/types'

export default function DashboardPage() {
  const {
    data: stats,
    isLoading: statsLoading,
    refetch: refetchStats,
    isFetching,
  } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn:  fetchDashboardStats,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  })

  const { data: recent = [] } = useQuery({
    queryKey: ['media-feed'],
    queryFn:  () => fetchRecentMedia(10),
    staleTime: 30 * 1000,
  })

  if (statsLoading) {
    return <div className="flex justify-center py-16"><Spinner size={28} /></div>
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-white font-bold">Live Dashboard</h2>
        <Button
          variant="ghost"
          size="sm"
          icon={<RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />}
          onClick={() => refetchStats()}
        >
          Refresh
        </Button>
      </div>

      {/* Stat cards */}
      {stats && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <StatCard emoji="📤" label="Total Uploads"  value={stats.total}                           />
            <StatCard emoji="🤖" label="AI Processed"   value={stats.processed}                       />
            <StatCard emoji="🎯" label="Avg Route Score" value={`${Math.round(stats.avgScore * 100)}%`} />
            <StatCard emoji="✨" label="Top Mood"        value={stats.topMood}       capitalize        />
          </div>

          {/* Today */}
          <Card className="flex items-center gap-4">
            <div className="text-3xl">📅</div>
            <div>
              <p className="text-white font-bold text-2xl">{stats.today}</p>
              <p className="text-room-muted text-sm">Uploads today</p>
            </div>
            <div className="ml-auto">
              {stats.today > 0 && (
                <span className="w-2.5 h-2.5 rounded-full bg-room-success animate-pulse-dot block" />
              )}
            </div>
          </Card>

          {/* Media type breakdown */}
          <Card>
            <p className="text-sm font-semibold text-room-text mb-3">By media type</p>
            <div className="space-y-2.5">
              {[
                { icon: <Image size={14} />,  label: 'Photos', count: stats.byType.photo },
                { icon: <Film  size={14} />,  label: 'Videos', count: stats.byType.video },
                { icon: <Mic   size={14} />,  label: 'Audio',  count: stats.byType.audio },
              ].map(({ icon, label, count }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className="text-room-muted">{icon}</div>
                  <span className="text-sm text-room-text w-14">{label}</span>
                  <div className="flex-1 h-1.5 bg-room-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-room-accent to-purple-400 rounded-full transition-all"
                      style={{ width: stats.total ? `${(count / stats.total) * 100}%` : '0%' }}
                    />
                  </div>
                  <span className="text-sm text-white w-6 text-right">{count}</span>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Recent items */}
      {recent.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-room-text">Recent uploads</h3>
          {recent.map(item => (
            <RecentRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  )
}

function StatCard({ emoji, label, value, capitalize }: {
  emoji: string; label: string; value: string | number; capitalize?: boolean
}) {
  return (
    <Card>
      <div className="text-2xl mb-2">{emoji}</div>
      <p className={`text-white font-bold text-xl ${capitalize ? 'capitalize' : ''}`}>{value}</p>
      <p className="text-room-muted text-xs mt-0.5">{label}</p>
    </Card>
  )
}

function RecentRow({ item }: { item: MediaItem }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-room-surface border border-room-border hover:border-room-muted transition-colors">
      {/* Thumb */}
      <div className="w-11 h-11 rounded-lg overflow-hidden bg-room-border shrink-0">
        {item.media_type === 'photo' ? (
          <img src={item.cloudinary_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xl">
            {item.media_type === 'video' ? '🎬' : '🎵'}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{item.file_name ?? 'Untitled'}</p>
        {item.ai_description && (
          <p className="text-room-muted text-xs truncate">{item.ai_description}</p>
        )}
      </div>

      {/* Score */}
      <div className="shrink-0 text-right space-y-1">
        {item.ai_route_score != null && (
          <div className="flex items-center gap-1 text-room-gold text-xs font-bold justify-end">
            <Zap size={10} />{Math.round(item.ai_route_score * 100)}%
          </div>
        )}
        <Badge variant={item.status === 'processed' ? 'success' : 'default'}>
          {item.status}
        </Badge>
      </div>
    </div>
  )
}
